import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BrokerClientApi, PaymentApi } from '@apply/api';
import { applicationInsights } from './ApplicationInsights';
import { paymentAgreement, PaymentReason } from '@apply/constants';
import {
    Question,
    QuestionType,
    ShowIf,
    FormValidationMetadata,
    stateSelectorValues,
    CardPaymentPanel,
    CardInfo,
} from '@apply/ui';
import { logApplicationInsightsError } from './common/functions/applicationInsightFunctions';
import { Header } from './Header';

const InterestAndOrExtension = 'Interest and/or Extension';
const paymentPurposeDropDown: { value: string; display: string }[] = [
    { value: null, display: '' },
    {
        value: 'Appraisal + Underwriting Fee',
        display: 'Appraisal + Underwriting Fee',
    },
    { value: 'Appraisal', display: 'Appraisal' },
    { value: 'Underwriting Fee', display: 'Underwriting Fee' },
    { value: InterestAndOrExtension, display: InterestAndOrExtension },
    { value: 'Other', display: 'Other' },
];

const emptyBillingDetails: PaymentApi.BillingDetails = {
    address: {
        city: '',
        country: 'US',
        line1: '',
        line2: '',
        postal_code: '',
        state: '',
    },
    email: '',
    name: '',
    phone: '',
};

export const Payment: React.FunctionComponent = () => {
    const [displayNextPage, set_displayNextPage] = useState<boolean>(false);
    const [submitted, set_submitted] = useState<boolean>(false);
    const [billingDetails, set_billingDetails] = useState<PaymentApi.BillingDetails>(emptyBillingDetails);
    const [paymentPurpose, set_paymentPurpose] = useState<string>('');
    const [isPropertyInArizona, set_isPropertyInArizona] = useState<boolean>(true);
    const [amount, set_amount] = useState<number>(0);
    const [memo, set_memo] = useState<string>('');
    const [combinedAddress, set_combinedAddress] = useState<string>('');

    const [validation, set_validation] = useState<FormValidationMetadata>({});

    const onQuestionValidate = useCallback((selector: string, passing: boolean, message: string) => {
        set_validation((state) => {
            const record = {
                ...state[selector],
                passing,
                failureMessage: message,
            };
            return {
                ...state,
                [selector]: record,
            };
        });
    }, []);

    useEffect(() => {
        const { address } = billingDetails;
        set_combinedAddress([address.line1, address.line2, `${address.city}, ${address.state} ${address.postal_code}`].join(' '));
    }, [billingDetails]);

    const nextButtonClick = async () => {
        set_submitted(true);

        const failureMessages: string[] = [];
        for (const fieldName in validation) {
            if (!validation[fieldName]?.passing) {
                /* istanbul ignore else */
                if (validation[fieldName]?.failureMessage) {
                    failureMessages.push(validation[fieldName]?.failureMessage);
                }
            }
        }

        if (failureMessages.length > 0) {
            console.error('form validation failed', failureMessages);
            return;
        }

        set_displayNextPage(true);
    };

    const transactionFee = amount * 0.03;
    const isNextDisabled =
        billingDetails.address.state == 'AZ' &&
        isPropertyInArizona &&
        ['Appraisal + Underwriting Fee', 'Appraisal', 'Underwriting Fee'].includes(paymentPurpose);
    return (
        <>
            <Header />
            <main>
                <section>
                    <h2>Builder Finance Inc. Payments</h2>
                    <ShowIf showIf={!displayNextPage}>
                        <div className='form-group'>
                            <div className='row'>
                                <Question
                                    type={QuestionType.Text}
                                    submitted={submitted}
                                    selector='payorName'
                                    placeholder='John Q Smith'
                                    styleName='ungrouped'
                                    onQuestionValidate={onQuestionValidate}
                                    value={billingDetails.name}
                                    onChange={(newvalue: string) =>
                                        set_billingDetails((previous) => ({
                                            ...previous,
                                            name: newvalue,
                                        }))
                                    }
                                >
                                    Account Holder/Name on Card
                                </Question>
                            </div>
                        </div>

                        <div className='form-group'>
                            <div className='row'>
                                <Question
                                    type={QuestionType.Phone}
                                    submitted={submitted}
                                    selector='payorPhone'
                                    placeholder='(212) 555-1212'
                                    styleName='ungrouped'
                                    onQuestionValidate={onQuestionValidate}
                                    value={billingDetails.phone}
                                    onChange={(newvalue: string) =>
                                        set_billingDetails((previous) => ({
                                            ...previous,
                                            phone: newvalue,
                                        }))
                                    }
                                >
                                    Phone Number
                                </Question>
                            </div>
                        </div>

                        <div className='form-group'>
                            <div className='row'>
                                <Question
                                    type={QuestionType.Email}
                                    submitted={submitted}
                                    selector='payorEmail'
                                    placeholder='jsmith@gmail.com'
                                    styleName='ungrouped'
                                    onQuestionValidate={onQuestionValidate}
                                    value={billingDetails.email}
                                    onChange={(newvalue: string) =>
                                        set_billingDetails((previous) => ({
                                            ...previous,
                                            email: newvalue,
                                        }))
                                    }
                                >
                                    Email Address
                                </Question>
                            </div>
                        </div>

                        <div className='form-group visual-group'>
                            <fieldset>
                                <legend className='header'>Billing Address</legend>
                                <div className='row'>
                                    <Question
                                        type={QuestionType.Text}
                                        submitted={submitted}
                                        selector='billingStreetAddressLine1'
                                        placeholder='123 Main Street'
                                        styleName='grouped'
                                        onQuestionValidate={onQuestionValidate}
                                        value={billingDetails.address.line1}
                                        onChange={(newvalue: string) =>
                                            set_billingDetails((previous) => ({
                                                ...previous,
                                                address: {
                                                    ...previous.address,
                                                    line1: newvalue,
                                                },
                                            }))
                                        }
                                    >
                                        <span className='sr-only'>Billing</span> Street Address
                                    </Question>
                                    <Question
                                        type={QuestionType.Text}
                                        submitted={submitted}
                                        selector='billingStreetAddressLine2'
                                        placeholder='Suite 123'
                                        styleName='grouped'
                                        value={billingDetails.address.line2}
                                        onChange={(newvalue: string) =>
                                            set_billingDetails((previous) => ({
                                                ...previous,
                                                address: {
                                                    ...previous.address,
                                                    line2: newvalue,
                                                },
                                            }))
                                        }
                                    >
                                        <span className='sr-only'>Billing Street Address, Line 2</span>
                                    </Question>
                                </div>
                                <div className='row'>
                                    <div className='columns small-12 medium-4'>
                                        <Question
                                            type={QuestionType.Text}
                                            submitted={submitted}
                                            selector='billingAddressCity'
                                            placeholder='Hamilton'
                                            styleName='grouped'
                                            onQuestionValidate={onQuestionValidate}
                                            value={billingDetails.address.city}
                                            onChange={(newvalue: string) =>
                                                set_billingDetails((previous) => ({
                                                    ...previous,
                                                    address: {
                                                        ...previous.address,
                                                        city: newvalue,
                                                    },
                                                }))
                                            }
                                        >
                                            <span className='sr-only'>Billing</span> City
                                        </Question>
                                    </div>
                                    <div className='columns small-6 medium-4'>
                                        <Question
                                            type={QuestionType.ColumnSelect}
                                            selector='billingAddressState'
                                            styleName='grouped'
                                            submitted={submitted}
                                            onQuestionValidate={onQuestionValidate}
                                            value={billingDetails.address.state}
                                            values={stateSelectorValues}
                                            onChange={(newvalue: string) =>
                                                set_billingDetails((previous) => ({
                                                    ...previous,
                                                    address: {
                                                        ...previous.address,
                                                        state: newvalue,
                                                    },
                                                }))
                                            }
                                        >
                                            <span className='sr-only'>Billing</span> State
                                        </Question>
                                    </div>
                                    <div className='columns small-6 medium-4'>
                                        <Question
                                            type={QuestionType.Zipcode}
                                            submitted={submitted}
                                            selector='billingAddressZipcode'
                                            placeholder='01234'
                                            styleName='grouped'
                                            onQuestionValidate={onQuestionValidate}
                                            value={billingDetails.address.postal_code}
                                            onChange={(newvalue: string) =>
                                                set_billingDetails((previous) => ({
                                                    ...previous,
                                                    address: {
                                                        ...previous.address,
                                                        postal_code: newvalue,
                                                    },
                                                }))
                                            }
                                        >
                                            <span className='sr-only'>Billing</span> Zip
                                        </Question>
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                        <ShowIf showIf={billingDetails.address.state == 'AZ'}>
                            <div className='form-group'>
                                <div
                                    className='row'
                                    style={{ marginRight: 20 }}
                                >
                                    <Question
                                        type={QuestionType.YesNo}
                                        selector='isPropertyInArizona'
                                        styleName='grouped'
                                        submitted={submitted}
                                        onQuestionValidate={onQuestionValidate}
                                        value={isPropertyInArizona}
                                        onChange={(newvalue: boolean) => set_isPropertyInArizona(newvalue)}
                                    >
                                        Is the Property in Arizona?
                                    </Question>
                                </div>
                            </div>
                        </ShowIf>

                        <div className='form-group'>
                            <div className='row'>
                                <Question
                                    type={QuestionType.RowSelect}
                                    selector='paymentPurpose'
                                    styleName='grouped'
                                    submitted={submitted}
                                    onQuestionValidate={onQuestionValidate}
                                    value={paymentPurpose}
                                    values={paymentPurposeDropDown}
                                    onChange={(newvalue: string) => set_paymentPurpose(newvalue)}
                                >
                                    Payment Purpose
                                </Question>
                            </div>
                        </div>

                        <div className='form-group'>
                            <div className='row'>
                                <Question
                                    type={QuestionType.Text}
                                    submitted={submitted}
                                    selector='memo'
                                    placeholder='e.g. property address'
                                    styleName='ungrouped'
                                    onQuestionValidate={onQuestionValidate}
                                    value={memo}
                                    onChange={(newvalue: string) => set_memo(newvalue)}
                                >
                                    Which Property / Loan
                                </Question>
                            </div>
                        </div>

                        <div className='form-group'>
                            <div className='row'>
                                <Question
                                    type={QuestionType.NonZeroCurrency}
                                    submitted={submitted}
                                    selector='paymentAmount'
                                    placeholder='$ 0.00'
                                    styleName='ungrouped'
                                    onQuestionValidate={onQuestionValidate}
                                    value={amount}
                                    onChange={(newvalue) => set_amount(newvalue)}
                                >
                                    Payment Amount
                                </Question>
                            </div>
                        </div>

                        <ShowIf showIf={paymentPurpose == InterestAndOrExtension}>
                            <div className='form-group'>
                                <div
                                    className='row'
                                    style={{ color: '#ab1715' }}
                                >
                                    <Question
                                        type={QuestionType.NonZeroCurrency}
                                        submitted={submitted}
                                        selector='paymentAmount'
                                        placeholder='$ 0.00'
                                        styleName='ungrouped'
                                        onQuestionValidate={onQuestionValidate}
                                        value={transactionFee}
                                        disabled={true}
                                    >
                                        Transaction Fee of 3%
                                    </Question>
                                </div>
                            </div>

                            <div className='form-group'>
                                <div
                                    className='row'
                                    style={{ fontWeight: 'bold' }}
                                >
                                    <Question
                                        type={QuestionType.NonZeroCurrency}
                                        submitted={submitted}
                                        selector='paymentAmount'
                                        placeholder='$ 0.00'
                                        styleName='ungrouped'
                                        onQuestionValidate={onQuestionValidate}
                                        value={Number(amount) + Number(transactionFee)}
                                        disabled={true}
                                    >
                                        Total Amount
                                    </Question>
                                </div>
                            </div>
                        </ShowIf>
                        <ShowIf showIf={isNextDisabled}>
                            <div
                                className='card-error'
                                role='alert'
                                style={{ marginTop: 20 }}
                            >
                                For property in Arizona, the appraisal cost and underwriting fee
                                <br />
                                should NOT be paid now; they should be at closing of the loan.
                            </div>
                        </ShowIf>
                        <div className='button-group'>
                            <button
                                onClick={nextButtonClick}
                                className='button'
                                disabled={isNextDisabled}
                            >
                                Next
                            </button>
                        </div>
                    </ShowIf>

                    <ShowIf showIf={displayNextPage}>
                        <CheckoutForm
                            billingDetails={billingDetails}
                            address={combinedAddress}
                            amount={Number(amount)}
                            memo={memo}
                            paymentPurpose={paymentPurpose}
                        />
                    </ShowIf>
                </section>
            </main>
        </>
    );
};

const CheckoutForm: React.FunctionComponent<{
    billingDetails: PaymentApi.BillingDetails;
    address?: string;
    memo?: string;
    paymentPurpose: string;
    amount?: number;
}> = ({ billingDetails, amount, memo, paymentPurpose }) => {
    const paymentGuid = useMemo(() => uuidv4(), []);

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [processing, setProcessing] = useState<boolean>(false);
    const [finished, setFinished] = useState<boolean>(false);

    const [disablePaymentButton, set_disablePaymentButton] = useState<boolean>(true);
    const [cardInfo, set_cardInfo] = useState<CardInfo>({
        cvc: '',
        expiry: '',
        number: '',
        cardType: undefined,
    });

    useEffect(() => {
        if (errorMessage) {
            logApplicationInsightsError(new Error(errorMessage));
        }
    }, [errorMessage]);

    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);
    const paymentApi = new PaymentApi.PaymentApi(applicationInsights);

    const submitPayment = async (event) => {
        event.preventDefault();

        setProcessing(true);

        let address = billingDetails.address.line1;
        if (billingDetails.address.line2) {
            address = `${address} ${billingDetails.address.line2}`;
        }

        const expirationTokens = cardInfo.expiry.split('/');

        const addressRequest: PaymentApi.AddressVerificationRequest = {
            paymentGuid: paymentGuid,
            cardNumber: cardInfo.number.replace(/ /g, ''),
            creditCardType: cardInfo.cardType,
            expirationDate: `20${expirationTokens[1]}-${expirationTokens[0]}`,
            creditDebitType: 'CREDIT',
            address: address,
            zipCode: billingDetails.address.postal_code,
            amount: amount,
        };

        const addressResponse = await paymentApi.verifyAddress(addressRequest);

        if (addressResponse.statusCode !== 200) {
            setProcessing(false);
            setErrorMessage('There was a system error validating the address.');
            return;
        }

        if (!addressResponse.result.isSuccess) {
            setProcessing(false);
            setErrorMessage(addressResponse.result.errorMessage);
            return;
        }

        const response = await brokerClientApi.getIpAddress();
        if (response.statusCode !== 200) {
            setProcessing(false);
            setErrorMessage('Could not calculate IP address.');
            return;
        }
        const ipAddress = response.result;

        const authorizationRequest: PaymentApi.AuthorizationRequest = {
            paymentGuid: paymentGuid,
            accountNumber: cardInfo.number.replace(/ /g, ''),
            name: billingDetails.name,
            phone: billingDetails.phone,
            email: billingDetails.email,
            address1: billingDetails.address.line1,
            address2: billingDetails.address.line2,
            city: billingDetails.address.city,
            state: billingDetails.address.state,
            zip: billingDetails.address.postal_code,
            cardType: cardInfo.cardType,
            expirationDate: `20${expirationTokens[1]}-${expirationTokens[0]}`,
            amount: amount,
            avsAuthResponseCode: addressResponse.result.avsResponseCode,
            paymentReason: PaymentReason.Custom,
            memo: `Custom Payment (${paymentPurpose}): ${memo}`,
            disclaimer: paymentAgreement,
            ipAddress: ipAddress,
        };

        const authorizationResponse = await paymentApi.authorization(authorizationRequest);

        if (authorizationResponse.statusCode !== 200) {
            setProcessing(false);
            setErrorMessage('There was a system error and the charge could not go through.');
            return;
        }

        if (!authorizationResponse.result.isSuccess) {
            setProcessing(false);
            setErrorMessage(authorizationResponse.result.errorMessage);
            return;
        }

        setProcessing(false);
        setFinished(true);
        setErrorMessage('');
    };

    const notSupportedCardType = cardInfo.cardType != 'VISA' && cardInfo.cardType != 'MASTERCARD';

    return (
        <>
            <ShowIf showIf={!finished}>
                <form
                    id='payment-form'
                    className='customPaymentForm'
                    onSubmit={submitPayment}
                >
                    <CardPaymentPanel
                        cardInfo={cardInfo}
                        set_cardInfo={set_cardInfo}
                    />
                    <label
                        htmlFor='rentalUnitsProvideLater'
                        className='customPaymentAgreement'
                        style={{ display: 'flex' }}
                    >
                        <input
                            id='paymentAgreementCheckbox'
                            type='checkbox'
                            onChange={(event) => set_disablePaymentButton(!event.target.checked)}
                            className='customPaymentCheckbox'
                        />
                        {paymentAgreement}
                    </label>
                    <div className='payment-button-container'>
                        <button
                            disabled={processing || disablePaymentButton || (notSupportedCardType && cardInfo.cardType)}
                            id='submit'
                            className='button'
                        >
                            <span id='button-text'>
                                {processing ? (
                                    <div
                                        className='spinner spinner-small'
                                        id='spinner'
                                    ></div>
                                ) : (
                                    'Pay now'
                                )}
                            </span>
                        </button>
                    </div>
                    {notSupportedCardType && cardInfo.cardType && (
                        <div
                            className='card-error'
                            role='alert'
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            We apologize, we currently only support Visa and Mastercard.
                        </div>
                    )}
                    <div
                        className='card-error'
                        role='alert'
                        style={{ display: 'flex', justifyContent: 'center' }}
                    >
                        {errorMessage}
                    </div>
                </form>
            </ShowIf>
            <ShowIf showIf={finished}>
                {/* Show a success message upon completion */}
                <div style={{ textAlign: 'center' }}>
                    <p role='alert'>Payment succeeded.</p>
                    <div className='button-group'>
                        <button
                            onClick={() => (window.location.href = '/payment')}
                            className='button result-button'
                        >
                            Make Another Payment
                        </button>
                        <button
                            onClick={() => (window.location.href = '/')}
                            className='button result-button'
                        >
                            Close
                        </button>
                    </div>
                </div>
            </ShowIf>
        </>
    );
};
